/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


class PagePagination extends CustomHTMLElement {
  connectedCallback() {
    if (this.hasAttribute("ajax") || this.hasAttribute("load-more") || this.hasAttribute("infinite")) {
      this.delegate.on("click", "a", this._onLinkClicked.bind(this));
    }

    if (this.hasAttribute("infinite")) {
      this.initInfiniteScroll();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  initInfiniteScroll() {
    const nextLink = this.querySelector("a[data-page]");
    if (!nextLink) return;
    this.observer?.disconnect();

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || this.loading) return;

        nextLink.click();
      },
      {
        rootMargin: "300px",
      }
    );

    this.observer.observe(nextLink);
  }

  _onLinkClicked(event, target) {
    event.preventDefault();

    if (this.hasAttribute("loading")) return;
    this.toggleAttribute("loading", true);
    const url = new URL(window.location.href);
    const page = target.dataset.page;

    url.searchParams.set("page", page);

    if (this.hasAttribute("load-more") || this.hasAttribute("infinite")) {
      triggerEvent(this, "pagination:load-more", {
        url: url.toString(),
        page,
      });

      return;
    }

    if (this.hasAttribute("ajax")) {
      triggerEvent(this, "pagination:page-changed", {
        url: url.toString(),
        page,
      });
    }
  } 
}

window.customElements.define("page-pagination", PagePagination);

var ProductFacet = class extends CustomHTMLElement {
  connectedCallback() {
    this.delegate.on("pagination:page-changed", this._rerender.bind(this));
    this.delegate.on("pagination:load-more", this._loadMore.bind(this));
    this.delegate.on("facet:criteria-changed", this._rerender.bind(this));
    this.delegate.on("facet:abort-loading", this._abort.bind(this));
  }

  async _rerender(event) {
    history.replaceState({}, "", event.detail.url);

    this._abort();
    this.showLoadingBar();

    const url = new URL(event.detail.url);

    url.searchParams.set("section_id", this.getAttribute("section-id"));

    try {
      this.abortController = new AbortController();

      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
      });

      const responseAsText = await response.text();

      const fakeDiv = document.createElement("div");
      fakeDiv.innerHTML = responseAsText;

      const newFacetMain = fakeDiv.querySelector("#facet-main");

      if (!newFacetMain) {
        throw new Error("Missing #facet-main in response");
      }

      this.querySelector("#facet-main").innerHTML = newFacetMain.innerHTML;

      this._updateFilters(fakeDiv);

      const scrollTarget =
        this.querySelector(".product-facet__meta-bar") ||
        this.querySelector(".product-facet__product-list") ||
        this.querySelector(".product-facet__main");

      if (scrollTarget) {
        requestAnimationFrame(() => {
          scrollTarget.scrollIntoView({
            block: "start",
            behavior: "smooth",
          });
        });
      }

      this.hideLoadingBar();
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error(e);
      }

      this.hideLoadingBar();
    }
  }

  async _loadMore(event) {
    history.replaceState({}, "", event.detail.url);

    this._abort();
    this.showLoadingBar();


    const url = new URL(event.detail.url);

    url.searchParams.set("section_id", this.getAttribute("section-id"));

    try {
      this.abortController = new AbortController();

      const response = await fetch(url.toString(), {
        signal: this.abortController.signal,
      });

      const responseAsText = await response.text();

      const fakeDiv = document.createElement("div");
      fakeDiv.innerHTML = responseAsText;

      const currentProductList = this.querySelector(".product-list__inner");

      const newProductList = fakeDiv.querySelector(".product-list__inner");

      if (!currentProductList || !newProductList) {
        throw new Error("Product list not found");
      }

      currentProductList.insertAdjacentHTML(
        "beforeend",
        newProductList.innerHTML
      );

      const currentPagination = this.querySelector("page-pagination");

      const newPagination = fakeDiv.querySelector("page-pagination");

      if (currentPagination) {
        if (newPagination) {
          currentPagination.replaceWith(newPagination);
        } else {
          currentPagination.remove();
        }
      }
      this.hideLoadingBar();
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error(e);
      }

      this.hideLoadingBar();
    }
  }

  _updateFilters(fakeDiv) {
    const activeFilterList = fakeDiv.querySelectorAll(
      ".product-facet__active-list"
    );

    const toolbarItem = document.querySelector(
      ".mobile-toolbar__item--filters"
    );

    if (toolbarItem) {
      toolbarItem.classList.toggle("has-filters", activeFilterList.length > 0);
    }

    const filtersTempDiv = fakeDiv.querySelector("#facet-filters");

    const currentFilters = this.querySelector("#facet-filters");

    if (!filtersTempDiv || !currentFilters) {
      return;
    }

    const drawerContent = currentFilters.querySelector(".drawer__content");

    const previousScrollTop = drawerContent ? drawerContent.scrollTop : 0;

    this.querySelectorAll(
      "#facet-filters-form .collapsible-toggle[aria-controls]"
    ).forEach((filterToggle) => {
      const targetId = filterToggle.getAttribute("aria-controls");

      const tempToggle = filtersTempDiv.querySelector(
        `[aria-controls="${targetId}"]`
      );

      if (!tempToggle) {
        return;
      }

      const isExpanded = filterToggle.getAttribute("aria-expanded") === "true";

      tempToggle.setAttribute("aria-expanded", isExpanded);

      const content = tempToggle.nextElementSibling;

      if (content) {
        content.toggleAttribute("open", isExpanded);
        content.style.overflow = isExpanded ? "visible" : "";
      }
    });

    currentFilters.innerHTML = filtersTempDiv.innerHTML;

    const updatedDrawerContent =
      currentFilters.querySelector(".drawer__content");

    if (updatedDrawerContent) {
      updatedDrawerContent.scrollTop = previousScrollTop;
    }
  }

  _abort() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
};

window.customElements.define("product-facet", ProductFacet);

document.addEventListener("DOMContentLoaded", () => {
  initZoom();
  initDeliveryCheck();
});

function initZoom() {
 document.addEventListener("click", (e) => {
   const item = e.target.closest(".product__media-item");
   if (!item) return;

   const container = item.closest("product-media");
   if (!container) return;

   if (e.target.closest("button")) return;

   if (item.dataset.mediaType !== "image") return;

   e.preventDefault();

   const items = container.querySelectorAll(".product__media-item");

   items.forEach((el) => {
     el.classList.remove("is-selected");
   });

   item.classList.add("is-selected");

   const zoomBtn = container.querySelector(
     ".product__zoom-button:not(.product__zoom-button--item)"
   );

   if (zoomBtn) {
     zoomBtn.click();
   }
 });
}


// delivery check function
function initDeliveryCheck() {
  const container = document.querySelector(".delivery_check--container");
  console.log(container);
  if (!container) return;

  const input = container.querySelector(".delivery_check--input");

  const button = container.querySelector(".delivery_check--input-button");

  const message = container.querySelector(".delivery_check--message");

  const messageText = message.querySelector(".delivery_check--message-text");

  const API_URL = "https://tcf-api.delhivery.workers.dev";

  let isLocked = false;

  function setButton(text, disabled = false) {
    button.innerText = text;
    button.disabled = disabled;
  }

  function showMessage(type, text) {
    message.className = `delivery_check--message ${type}`;
    messageText.innerHTML = text;
    message.style.display = "inline-flex";
  }

  function hideMessage() {
    message.style.display = "none";
  }

  function formatDeliveryDate(tat) {
    const deliveryDate = new Date();

    deliveryDate.setDate(deliveryDate.getDate() + tat + 3);

    return deliveryDate.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  }

  function lockInput() {
    isLocked = true;

    input.disabled = true;

    setButton("Edit");
  }

  function unlockInput() {
    isLocked = false;
    input.disabled = false;
    input.focus();
    setButton("Check", input.value.length !== 6);

    hideMessage();
  }

  function sanitizePincode(value) {
    return value.replace(/\D/g, "");
  }

  async function fetchDelivery(pincode) {
    const response = await fetch(`${API_URL}?pincode=${pincode}`);

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error("Invalid pincode or delivery is not available");
    }

    return data;
  }

  async function checkDelivery() {
    const pincode = input.value.trim();

    if (pincode.length !== 6) return;

    setButton("Checking...", true);

    try {
      const data = await fetchDelivery(pincode);

      const formattedDate = formatDeliveryDate(Number(data.data.tat));

      showMessage("success", `Delivery by ${formattedDate}`);

      lockInput();
    } catch (error) {
      showMessage(
        "error",
        error instanceof Error ? error.message : "Delivery unavailable"
      );

      setButton("Check");
    }
  }

  input.addEventListener("input", () => {
    if (isLocked) return;

    input.value = sanitizePincode(input.value);

    setButton("Check", input.value.length !== 6);

    hideMessage();
  });

  button.addEventListener("click", () => {
    if (isLocked) {
      unlockInput();
      return;
    }

    checkDelivery();
  });

  setButton("Check", true);
}
